import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell, GoogleButton } from "@/components/AuthParts";
import { RolePicker } from "@/components/RolePicker";

const Field = ({ id, label, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} className="h-11 rounded-xl" data-testid={`register-${id}-input`} {...props} />
  </div>
);

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ role: params.get("peran") === "penyedia" ? "penyedia" : "pelanggan", name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await register(form);
      toast.success("Akun berhasil dibuat");
      navigate(user.role === "penyedia" ? "/dasbor/profil" : "/minta-penawaran", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Buat akun Tuntas" subtitle="Gratis, dan hanya butuh satu menit.">
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <RolePicker value={form.role} onChange={(role) => setForm({ ...form, role })} />
        <Field id="name" label={form.role === "penyedia" ? "Nama usaha / nama lengkap" : "Nama lengkap"} required value={form.name} onChange={set("name")} />
        <Field id="email" label="Email" type="email" required value={form.email} onChange={set("email")} placeholder="nama@email.com" />
        <Field id="phone" label="Nomor WhatsApp" type="tel" value={form.phone} onChange={set("phone")} placeholder="08xxxxxxxxxx" />
        <Field id="password" label="Kata sandi (min. 6 karakter)" type="password" required minLength={6} value={form.password} onChange={set("password")} />
        {error && <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="register-submit-button" className="h-11 w-full rounded-xl bg-teal-700 font-semibold hover:bg-teal-800">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Daftar
        </Button>
      </form>
      <div className="my-6 flex items-center gap-3 text-xs text-stone-400">
        <span className="h-px flex-1 bg-stone-200" /> atau <span className="h-px flex-1 bg-stone-200" />
      </div>
      <GoogleButton label="Daftar dengan Google" />
      <p className="mt-6 text-center text-sm text-stone-500">
        Sudah punya akun? <Link to="/masuk" className="font-semibold text-teal-700 hover:underline" data-testid="register-to-login-link">Masuk</Link>
      </p>
    </AuthShell>
  );
}
